/** Official Telegram Mini Apps WebApp helpers. Never invent initData on the client. */

export type TelegramWebApp = {
  initData?: string;
  initDataUnsafe?: {
    user?: { id?: number; username?: string; first_name?: string; language_code?: string };
    start_param?: string;
  };
  ready: () => void;
  expand: () => void;
  close?: () => void;
  viewportHeight?: number;
  viewportStableHeight?: number;
  isExpanded?: boolean;
  colorScheme?: 'light' | 'dark';
  themeParams?: Record<string, string>;
  version?: string;
  isVersionAtLeast?: (version: string) => boolean;
  disableVerticalSwipes?: () => void;
  enableVerticalSwipes?: () => void;
  isVerticalSwipesEnabled?: boolean;
  requestFullscreen?: () => void;
  isFullscreen?: boolean;
  BackButton?: {
    show: () => void;
    hide: () => void;
    onClick: (fn: () => void) => void;
    offClick: (fn: () => void) => void;
    isVisible?: boolean;
  };
  onEvent?: (event: string, fn: () => void) => void;
  offEvent?: (event: string, fn: () => void) => void;
};

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function getTelegramWebApp(): TelegramWebApp | null {
  try {
    return window.Telegram?.WebApp ?? null;
  } catch {
    return null;
  }
}

export function isTelegramMiniApp(): boolean {
  const tg = getTelegramWebApp();
  return Boolean(tg?.initData);
}

export function applyTelegramViewport(): void {
  const tg = getTelegramWebApp();
  if (!tg) return;
  const height = tg.viewportStableHeight || tg.viewportHeight;
  if (height) {
    document.documentElement.style.setProperty('--tg-viewport-height', `${height}px`);
  }
  document.documentElement.classList.add('tg-miniapp');
  document.documentElement.dataset.tgColorScheme = tg.colorScheme || 'dark';
}

/** Prevent iOS Telegram from closing the Mini App on vertical overscroll (Bot API 7.7+). */
export function disableTelegramClosingSwipes(): void {
  const tg = getTelegramWebApp();
  if (!tg || typeof tg.disableVerticalSwipes !== 'function') return;
  try {
    if (typeof tg.isVersionAtLeast === 'function' && !tg.isVersionAtLeast('7.7')) return;
    tg.disableVerticalSwipes();
  } catch {
    // Older clients / desktop hosts may not support the method.
  }
}

export function bootTelegramShell(): void {
  const tg = getTelegramWebApp();
  if (!tg) return;
  try {
    tg.ready();
    tg.expand();
  } catch {
    // Host may not support expand on desktop Telegram.
  }
  disableTelegramClosingSwipes();
  applyTelegramViewport();
  tg.onEvent?.('viewportChanged', applyTelegramViewport);
}
